import { absolute, DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from './seo';
import { SOCIAL_URLS } from '@/data/navigation';

/**
 * Structured data, for the panels and bylines search engines build from it.
 *
 * Only claims that are true: there is no `SearchAction` here, because the blog
 * search is client-side and has no URL of its own — telling Google to send
 * people to a search page that does not exist is worse than not being asked.
 */

export interface Crumb {
    name: string;
    path: string;
}

/** The organisation itself. One per page is enough; the home and About pages carry it. */
export const organisationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'NGO',
    '@id': `${SITE_URL}/#organisation`,
    'name': SITE_NAME,
    'alternateName': 'Citizens Gavel',
    'url': SITE_URL,
    'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_URL}/og/citizens-gavel.png`,
        'width': 1200,
        'height': 630,
    },
    'description':
        'Citizens’ Gavel is a Nigerian civic-technology organisation that speeds up access to justice. Pro bono legal aid, court performance data and tools that move real cases.',
    'foundingDate': '2017',
    'areaServed': { '@type': 'Country', 'name': 'Nigeria' },
    'sameAs': Object.values(SOCIAL_URLS).filter(Boolean),
});

/** An article or a success story. */
export function articleSchema(
    item: {
        title: string;
        slug: string;
        excerpt?: string;
        image?: string;
        author?: string;
        publishedAt?: string;
        tags?: string[];
    },
    basePath: string,
) {
    const url = absolute(`${basePath}/${item.slug}`);
    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': item.title.slice(0, 110),
        'description': item.excerpt || undefined,
        'image': absolute(item.image || DEFAULT_OG_IMAGE),
        'datePublished': item.publishedAt || undefined,
        'author': item.author
            ? { '@type': 'Person', 'name': item.author }
            : { '@type': 'Organization', 'name': SITE_NAME },
        'publisher': { '@id': `${SITE_URL}/#organisation` },
        'mainEntityOfPage': { '@type': 'WebPage', '@id': url },
        url,
        'keywords': item.tags?.length ? item.tags.join(', ') : undefined,
    };
}

/**
 * The site as a thing in its own right. Google reads this for the site name it
 * prints above a result, in place of guessing one from the domain. No
 * `SearchAction`, for the reason given at the top of this file.
 */
export const webSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    'name': SITE_NAME,
    'alternateName': 'Citizens Gavel',
    'url': SITE_URL,
    'inLanguage': 'en-NG',
    'publisher': { '@id': `${SITE_URL}/#organisation` },
});

/**
 * The help page's questions and answers.
 *
 * Worth being accurate about what this buys: since 2023 Google has shown FAQ
 * rich results only for government and health sites, so this will not draw an
 * expanding panel under the result. It stays because it is valid, it costs a few
 * hundred bytes, and it is how a machine reading the page learns that these are
 * questions with answers rather than a list of headings.
 */
export const faqSchema = (faqs: { question: string; answer: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': { '@type': 'Answer', 'text': faq.answer },
    })),
});

/** The trail shown under a result. The final crumb is the page itself. */
export const breadcrumbSchema = (trail: Crumb[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': trail.map((crumb, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'name': crumb.name,
        'item': absolute(crumb.path),
    })),
});
